import React, { useEffect, useMemo, useState } from "react";
import { Col, Card, CardBody, Row } from "reactstrap";
import { Link } from "react-router-dom";
import CountUp from "react-countup";
import { getPunchLocation } from "../../utils/terminalMap";
import api from "../../api";

const Widgets = () => {
  const [attendanceInfo, setAttendanceInfo] = useState({
    status: "",
    terminalId: null,
    computer: "",
  });

  const authUser = sessionStorage.getItem("authUser");
  const empId = authUser ? JSON.parse(authUser)?.uid : null;

  useEffect(() => {
    const fetchLastAction = async () => {
      try {
        const response = await api.get(
          `WhosInBuilding/today-last-punch/${empId}`
        );

        console.log("Fetch Last Action Response :", response.data);

        if (response.data) {
          setAttendanceInfo(response.data);
        }
      } catch (error) {
        console.error("Error fetching last action:", error);
      }
    };

    if (empId) fetchLastAction();
  }, [empId]);

  const baseWidgets = [
    {
      id: 1,
      label: "Attendance",
      counter: "36",
      link: "View Attendance",
      route: "/time-and-attendance",
      icon: "bx bx-time-five",
      iconBg: "#E6F1FB",
      iconColor: "#185FA5",
      trend: "↑ 4% this week",
      trendColor: "#1D9E75",
      decimals: 0,
      prefix: "",
      separator: ",",
      suffix: "",
    },
    {
      id: 2,
      label: "EPOS",
      counter: "36",
      link: "View all EPOs",
      route: "/epo",
      icon: "bx bx-receipt",
      iconBg: "#E1F5EE",
      iconColor: "#0F6E56",
      trend: "↑ 2 new today",
      trendColor: "#1D9E75",
      decimals: 0,
      prefix: "",
      separator: ",",
      suffix: "",
    },
    {
      id: 3,
      label: "Leaves",
      counter: "13",
      link: "See details",
      route: "/leaves",
      icon: "bx bx-calendar-minus",
      iconBg: "#FAEEDA",
      iconColor: "#BA7517",
      trend: "2 pending approval",
      trendColor: "#BA7517",
      decimals: 0,
      prefix: "",
      separator: ",",
      suffix: " days",
    },
    {
      id: 4,
      label: "Tasks",
      counter: "11",
      link: "View all tasks",
      route: "/tasks",
      icon: "bx bx-task",
      iconBg: "#FCEBEB",
      iconColor: "#A32D2D",
      trend: "3 due today",
      trendColor: "#D85A30",
      decimals: 0,
      prefix: "",
      separator: ",",
      suffix: "",
    },
  ];
  const punchLocation = getPunchLocation(
    attendanceInfo.terminalId,
    attendanceInfo.computer
  );

  const ecomWidgets = useMemo(() => {
    return baseWidgets.map((w) =>
      w.id === 1
        ? {
          ...w,
          status: attendanceInfo.status || "...",
          location: punchLocation.label,
          locationIcon: punchLocation.icon,
          locationColor: punchLocation.color,
        }
        : w
    );
  }, [attendanceInfo, punchLocation]);

  const statusColor =
    attendanceInfo?.status?.toUpperCase() === "IN"
      ? "#1D9E75"
      : attendanceInfo?.status?.toUpperCase() === "OUT"
        ? "#D85A30"
        : "#888780";


  return (
    <Row>
      {ecomWidgets.map((item) => (
        <Col xl={3} md={6} key={item.id}>
          <Card style={styles.card}>
            <CardBody style={styles.cardBody}>

              {/* Top row — label + icon */}
              <div style={styles.topRow}>
                <p style={styles.label}>{item.label}</p>
                <div style={{ ...styles.iconCircle, background: item.iconBg }}>
                  <i className={item.icon} style={{ fontSize: "20px", color: item.iconColor }} />
                </div>
              </div>

              {/* Counter */}
              <h4 style={styles.counter}>
                <CountUp
                  start={0}
                  prefix={item.prefix}
                  suffix={item.suffix}
                  separator={item.separator}
                  end={parseFloat(item.counter)}
                  decimals={item.decimals}
                  duration={2}
                />
              </h4>

              {/* Bottom row — link + trend/status */}
              <div style={styles.bottomRow}>
                <Link to={item.route} style={styles.link}>
                  {item.link}
                </Link>

                {item.id === 1 ? (
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        ...styles.trend,
                        color: statusColor,
                        fontWeight: 700,
                        fontSize: "13px",
                      }}
                    >
                      {item.status || "..."}
                    </div>

                    <div
                      style={{
                        fontSize: "11px",
                        color: item.locationColor,
                        marginTop: "2px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        justifyContent: "flex-end",
                      }}
                    >
                      <i className={item.locationIcon}></i>
                      {item.location}
                    </div>
                  </div>
                ) : (
                  <span style={{ ...styles.trend, color: item.trendColor }}>
                    {item.trend}
                  </span>
                )}
              </div>

            </CardBody>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

const styles = {
  card: {
    borderRadius: "16px",
    border: "0.5px solid #e8e8e8",
    boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
    marginBottom: "20px",
  },
  cardBody: {
    padding: "20px 22px",
  },
  topRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "14px",
  },
  label: {
    margin: 0,
    fontSize: "12px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    color: "#888780",
  },
  iconCircle: {
    width: "42px",
    height: "42px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  counter: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#2C2C2A",
    margin: "0 0 14px",
  },
  bottomRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderTop: "0.5px solid #f0f0f0",
    paddingTop: "10px",
  },
  link: {
    fontSize: "12px",
    color: "#534AB7",
    textDecoration: "underline",
  },
  trend: {
    fontSize: "12px",
  },
};

export default Widgets;